import React, { useEffect, useState } from 'react';
import { RotateCcw, Heart, Repeat } from 'lucide-react';
import { OwnershipFilter } from './OwnershipFilterButtons';
import { useTranslation } from '../../hooks/useTranslation';
import { supabase } from '../../lib/supabase';

interface SetInfo {
  name: string;
  display_name: string;
  display_name_en?: string;
}

interface ExtendedFilterState {
  set: string | null;
  rarity: string | null;
  search: string;
  ownership: OwnershipFilter;
  showWanted?: boolean;
  showTradable?: boolean;
}

interface TradingFiltersProps {
  filters: ExtendedFilterState;
  onFilterChange: (filters: ExtendedFilterState) => void;
  sets: string[];
  rarities: string[];
  wantedCardRarity?: string;
  isWantedCardSection: boolean;
}

export function TradingFilters({
  filters,
  onFilterChange,
  sets,
  rarities,
  wantedCardRarity,
  isWantedCardSection
}: TradingFiltersProps) {
  const { t, lang } = useTranslation();
  const [setsInfo, setSetsInfo] = useState<Record<string, SetInfo>>({});
  
  useEffect(() => {
    const fetchSetsInfo = async () => {
      if (sets.length === 0) return;
      
      const { data } = await supabase
        .from('sets')
        .select('name, display_name, display_name_en')
        .in('name', sets);
        
      if (data) {
        const setsMap = data.reduce((acc, set) => {
          acc[set.name] = set;
          return acc;
        }, {} as Record<string, SetInfo>);
        setSetsInfo(setsMap);
      }
    };
    
    fetchSetsInfo();
  }, [sets]);
  
  const getSetDisplayName = (setInfo: SetInfo | undefined) => {
    if (!setInfo) return '';
    
    return lang === 'en' && setInfo.display_name_en 
      ? setInfo.display_name_en 
      : setInfo.display_name;
  };
  
  const handleReset = () => {
    onFilterChange({
      ...filters,
      set: null,
      rarity: null,
      search: '',
      ownership: 'all',
      showWanted: false,
      showTradable: false
    });
  };

  const handleFilterChange = (newFilter: {
    ownership?: OwnershipFilter;
    showWanted?: boolean;
    showTradable?: boolean;
  }) => {
    onFilterChange({
      ...filters,
      ownership: newFilter.ownership || 'all',
      showWanted: newFilter.showWanted || false,
      showTradable: newFilter.showTradable || false
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Champ de recherche */}
        <div className="flex-1">
          <input
            type="text"
            placeholder={t('create_trade.filters.search_placeholder')}
            value={filters.search}
            onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Filtres */}
        <div className="flex flex-col sm:flex-row gap-3 sm:w-auto">
          {sets.length > 0 && (
            <select
              value={filters.set || ''}
              onChange={(e) => onFilterChange({ ...filters, set: e.target.value || null })}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">{t('filters.filter_set')}</option>
              {sets.map((set) => (
                <option key={set} value={set}>
                  {setsInfo[set] ? getSetDisplayName(setsInfo[set]) : set}
                </option>
              ))}
            </select>
          )}

          {rarities.length > 0 && !wantedCardRarity && (
            <select
              value={filters.rarity || ''}
              onChange={(e) => onFilterChange({ ...filters, rarity: e.target.value || null })}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">{t('filters.filter_rarity')}</option>
              {rarities.map((rarity) => (
                <option key={rarity} value={rarity}>
                  {rarity}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          onClick={handleReset}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <RotateCcw className="w-4 h-4" />
          {t('filters.reset_button')}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <button
          onClick={() => handleFilterChange({ ownership: 'all' })}
          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
            filters.ownership === 'all' && !filters.showWanted && !filters.showTradable
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {t('create_trade.filters.ownership.all')}
        </button>

        {isWantedCardSection ? (
          <>
            <button
              onClick={() => handleFilterChange({ ownership: 'owned' })}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                filters.ownership === 'owned'
                  ? 'bg-purple-600 text-white'
                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
              }`}
            >
              {t('create_trade.filters.ownership.rare')}
            </button>
            <button
              onClick={() => handleFilterChange({ ownership: 'not_owned' })}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                filters.ownership === 'not_owned'
                  ? 'bg-orange-600 text-white'
                  : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
              }`}
            >
              {t('create_trade.filters.ownership.not_owned')}
            </button>
            <button
              onClick={() => handleFilterChange({ showWanted: true })}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors inline-flex items-center ${
                filters.showWanted
                  ? 'bg-red-600 text-white'
                  : 'bg-red-50 text-red-700 hover:bg-red-100'
              }`}
            >
              <Heart className="w-3.5 h-3.5 mr-1" />
              {t('create_trade.filters.show_wanted')}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => handleFilterChange({ ownership: 'owned' })}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                filters.ownership === 'owned'
                  ? 'bg-green-600 text-white'
                  : 'bg-green-50 text-green-700 hover:bg-green-100'
              }`}
            >
              {t('create_trade.filters.ownership.owned')}
            </button>
            <button
              onClick={() => handleFilterChange({ ownership: 'duplicates' })}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                filters.ownership === 'duplicates'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
            >
              {t('create_trade.filters.ownership.duplicates')}
            </button>
            <button
              onClick={() => handleFilterChange({ showTradable: true })}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors inline-flex items-center ${
                filters.showTradable
                  ? 'bg-green-600 text-white'
                  : 'bg-green-50 text-green-700 hover:bg-green-100'
              }`}
            >
              <Repeat className="w-3.5 h-3.5 mr-1" />
              {t('create_trade.filters.show_tradable')}
            </button>
          </>
        )}
      </div>
      
      {wantedCardRarity && (
        <div className="mt-4 mb-2 text-sm text-gray-600">
          {t('create_trade.filters.rarity')} : <span className="font-medium">{wantedCardRarity}</span>
        </div>
      )}
    </div>
  );
} 